import { DecimalPipe, UpperCasePipe } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
  Component,
  computed,
  inject,
  input,
  viewChildren,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAddressComponent,
  OnyxLanguagePipe,
  OnyxPhonePipe,
} from '@onyx/angular';
import { chain, isEqual, uniqBy } from 'lodash';
import { BUSINESS_HOURS_KEYS } from '../../../../../../../../../common/constants/common/business-hours-keys';
import { DAY_TIME_RANGE } from '../../../../../../../../../common/constants/common/day-time-range';
import { DictionaryCode } from '../../../../../../../../../common/enums/dictionary-code';
import { RouteHelper } from '../../../../../../../../../common/helpers/route.helper';
import type { BusinessHours } from '../../../../../../../../../common/interfaces/common/business-hours';
import { GoodQuantityPipe } from '../../../../../../../../../common/pipes/good-quantity.pipe';
import { OrderPointCategory } from '../../../../../../../common/enums/order-point-category';
import { OrderHelper } from '../../../../../../../common/helpers/order.helper';
import type { Order } from '../../../../../../../common/interfaces/order';
import type {
  OrderLoadingGood,
  OrderLoadingPoint,
  OrderPoint,
  OrderUnloadingPoint,
} from '../../../../../../../common/interfaces/order-point';

@Component({
  selector: 'app-order-pdf-template-route',
  imports: [
    TranslatePipe,
    UpperCasePipe,
    OnyxAddressComponent,
    OnyxLanguagePipe,
    OnyxPhonePipe,
    GoodQuantityPipe,
    DecimalPipe,
  ],
  templateUrl: './order-pdf-template-route.component.html',
  styleUrl: './order-pdf-template-route.component.scss',
})
export class OrderPdfTemplateRouteComponent {
  private readonly orderHelper = inject(OrderHelper);

  public readonly order = input.required<Order>();

  protected readonly I18N = 'orders.pdfTemplate';
  protected readonly BUSINESS_HOURS_KEYS = BUSINESS_HOURS_KEYS;

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OrderPointCategory = OrderPointCategory;

  protected orderPoints = computed(() =>
    this.order().points.filter((point) => RouteHelper.isIncludedInOrder(point)),
  );
  protected loadingPoints = computed<OrderLoadingPoint[]>(() =>
    this.order().points.filter(
      (point) => point.category === OrderPointCategory.LOADING,
    ),
  );
  protected unloadingPoints = computed<OrderUnloadingPoint[]>(() =>
    this.order().points.filter(
      (point) => point.category === OrderPointCategory.UNLOADING,
    ),
  );

  protected lastPointItemHeight = computed(() => {
    const items = this.routeItems();
    if (!items.length) return 0;

    const lastItem = items.at(-1);
    return lastItem?.nativeElement?.clientHeight || 0;
  });

  protected pointsGoods = computed(
    () =>
      new Map(
        this.orderPoints().map((point) => [
          point.uuid,
          this.getFormGoods(point),
        ]),
      ),
  );

  private routeItems = viewChildren<ElementRef<HTMLElement>>('routeItem');

  protected businessHoursAlwaysOpen(businessHours: BusinessHours): boolean {
    return Object.values(businessHours).every((openingHours) =>
      isEqual(openingHours, DAY_TIME_RANGE),
    );
  }

  protected loadingPointGoods(): OrderLoadingGood[] {
    return this.order()
      .points.filter((point) => point.category === OrderPointCategory.LOADING)
      .map((point) => point.goods)
      .flatMap((goods) => uniqBy(goods, 'uuid'));
  }

  protected unloadingPointGoods(point: OrderPoint): OrderLoadingGood[] {
    if (point.category !== OrderPointCategory.UNLOADING) return [];

    const unloadingGoods = point.goods;
    const loadingsGoods = this.loadingPoints()?.flatMap((point) => point.goods);
    if (!unloadingGoods) return [];

    return loadingsGoods?.map((loadingGood) => {
      const unloadingGood = unloadingGoods.find(
        (good) => good.uuid === loadingGood.uuid,
      );

      return {
        ...loadingGood,
        quantity: unloadingGood
          ? unloadingGood.quantity
          : OrderHelper.getGoodQuantity(loadingGood),
      };
    });
  }

  protected getTimeWindows(point: OrderPoint): {
    label: string;
    value: string[];
  } {
    return this.orderHelper.getTimeWindows(point);
  }

  private getFormGoods(point: OrderPoint): OrderLoadingGood[] {
    if (point.category === OrderPointCategory.LOADING) return point.goods;
    if (point.category !== OrderPointCategory.UNLOADING) return [];

    return chain(point.goods)
      .map(({ uuid, quantity }) => {
        const good = this.loadingPointGoods().find(
          (good) => good.uuid === uuid,
        );
        if (!good) return null;

        return { ...good, quantity };
      })
      .compact()
      .value();
  }
}
